import styled from "styled-components";
import {NAV_COLLAPSE_SECTION_MAX_WIDTH} from "nav/navCollapse";
import {brand, motion, radii, shadows, zIndex} from "theme/tokens";

/**
 * The persistent left-edge feedback tab (ui#1085).
 *
 * Four choices are load-bearing rather than cosmetic:
 *
 * - **`zIndex.nav`, not a new layer.** The tab is in-page chrome, so it rides the SAME rung as the
 *   floating top bar on the shared ladder in `theme/tokens.ts`. That is what satisfies "above the
 *   content, never over a modal" by construction: `modal` is 1300 and this is 30, so an open dialog
 *   and its scrim always cover the tab that opened them — no per-dialog hiding logic to keep in sync.
 * - **Desktop only.** Below the breakpoint the tab is `display: none` and the dialog stays reachable
 *   through the Resources → Support entry, because a permanently docked control on a phone is an
 *   obstruction over the content it floats on rather than an affordance. The bound is the top bar's
 *   OWN widest collapse edge rather than a new round number: above it every nav item is back in the
 *   pill, so the tab appears exactly where the chrome has stopped running out of room, and adding a
 *   collapsible item moves both together instead of leaving this query stale.
 * - **`brand.live` on `brand.navy`.** The green is the EXISTING brand green (the live/online dot,
 *   which `toast.success` already maps to) rather than a newly minted hex, and the ink is the
 *   existing deep navy — white on this green measures ~2.2:1 and fails, while navy on it measures
 *   ~8:1. This is the same accent-plus-owned-ink pairing `goldInk` and `upgradeInk` exist for.
 * - **`writing-mode: vertical-rl` without a rotation.** The label runs down the edge; rotating the
 *   box instead would flip which side the rounded corners and the padding land on, which is how these
 *   tabs usually end up with their radius on the off-screen edge.
 */
export const EdgeTab = styled.button`
	position: fixed;
	left: 0;
	top: 50%;
	transform: translateY(-50%);
	z-index: ${zIndex.nav};

	display: flex;
	align-items: center;
	justify-content: center;
	writing-mode: vertical-rl;
	padding: 16px 7px;
	border: 0;
	/* Square against the viewport edge, rounded where it meets the page. */
	border-radius: 0 ${radii.control} ${radii.control} 0;
	background: ${brand.live};
	color: ${brand.navy};
	font: inherit;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.04em;
	cursor: pointer;
	/* The in-page elevation for this rung. NOT shadows.modal: a 40px-down, 100px-blur modal shadow under
	   a ~34px tab reads as a smudge, and the tab is chrome on the nav rung rather than an overlay. */
	box-shadow: ${shadows.popover};
	transition: padding-right ${motion.hover} ${motion.easeOut}, filter ${motion.hover} ${motion.easeOut};

	&:hover {
		/* Grows INTO the page on hover — the left edge is pinned at 0, so the right padding is the only
		   side that can move. A size nudge rather than a color change, so the green keeps the contrast
		   ratio the ink was chosen against. (No backticks in here: this is inside a styled-components
		   template literal, where one would end the CSS and produce a syntax error instead.) */
		padding-right: 11px;
		filter: brightness(1.04);
	}

	/* The focus ring must clear a control flush against the viewport edge, so it is drawn INSIDE. */
	&:focus-visible {
		outline: 2px solid ${brand.navy};
		outline-offset: -4px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}

	/* Phones and small tablets reach the same dialog through Resources → Support. */
	@media (max-width: ${NAV_COLLAPSE_SECTION_MAX_WIDTH}px) {
		display: none;
	}
`;
